import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useSiteContext } from "../components/layout/Layout";
import { ComplianceTable } from "../components/acms/Shared";
import { StatusCircle, PressureGauge } from "../components/acms/Indicators";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, CartesianGrid } from "recharts";

function Card({ title, accent, children, testid }) {
  return (
    <div className="glass p-5 rounded-lg" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs uppercase tracking-wider text-gray-600 font-bold">{title}</h3>
        {accent && <span className="text-[10px] uppercase tracking-widest f360-gradient-text font-bold">{accent}</span>}
      </div>
      {children}
    </div>
  );
}

export default function SprinklerPage() {
  const { selectedSite, sites } = useSiteContext();
  const [tele, setTele] = useState(null);
  const [hist, setHist] = useState([]);
  const [sprinklerPumps, setSprinklerPumps] = useState([]);
  const [sprinklerSensors, setSprinklerSensors] = useState([]);
  const [sprinklerValves, setSprinklerValves] = useState([]);
  const [liveValues, setLiveValues] = useState({});

  useEffect(() => {
    if (!selectedSite) return;
    const load = () => {
      api.get(`/telemetry/${selectedSite}`).then((r) => setTele(r.data)).catch(() => {});
      api.get(`/assets`, { params: { site_id: selectedSite, asset_type: "pump" } }).then((r) => {
        const sprinklerOnly = (r.data || []).filter((p) => p.system === "sprinkler" || p.meta?.system === "sprinkler");
        setSprinklerPumps(sprinklerOnly);
      }).catch(() => {});
      api.get(`/assets`, { params: { site_id: selectedSite, asset_type: "pressure_sensor", system: "sprinkler" } }).then((r) => setSprinklerSensors(r.data || [])).catch(() => {});
      api.get(`/assets`, { params: { site_id: selectedSite, asset_type: "valve", system: "sprinkler" } }).then((r) => setSprinklerValves(r.data || [])).catch(() => {});
      api.get(`/assets/live-values`, { params: { site_id: selectedSite } }).then((r) => setLiveValues(r.data || {})).catch(() => {});
    };
    load();
    const i = setInterval(load, 5000);
    return () => clearInterval(i);
  }, [selectedSite]);

  // 24h trend for the first gateway-linked sprinkler pressure sensor — real register history,
  // not the old `tele.ch123` field which never existed in the telemetry simulator (always 0).
  const trendSensor = sprinklerSensors.find((s) => s.gateway_id && s.io_point_name);
  useEffect(() => {
    if (!selectedSite || !trendSensor) { setHist([]); return; }
    let cancelled = false;
    const load = () => {
      api.get(`/v1/sites/${selectedSite}/telemetry/parameter-history`, { params: { parameter: trendSensor.io_point_name, hours: 24 } })
        .then((r) => {
          if (cancelled) return;
          setHist((r.data || []).map((d) => ({ ...d, time: new Date(d.ingested_at || d.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) })));
        }).catch(() => {});
    };
    load();
    const i = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(i); };
  }, [selectedSite, trendSensor]);

  if (!tele) return <div className="text-gray-500">Loading sprinkler telemetry…</div>;
  const site = sites.find((s) => s.site_id === selectedSite);

  return (
    <div className="space-y-6" data-testid="sprinkler-page">
      <div>
        <div className="text-xs text-gray-500">Dashboard → {site?.name} → Sprinkler System</div>
        <h1 className="text-3xl md:text-4xl font-extrabold">Sprinkler System</h1>
        <p className="text-sm text-gray-500 mt-1">GR Appendix d · live telemetry · auto-refresh 5s</p>
      </div>


      {sprinklerPumps.length > 0 && (
        <section>
          <h2 className="text-sm uppercase tracking-[0.2em] text-gray-500 mb-3">Registered Sprinkler Pumps</h2>
          <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
            {sprinklerPumps.map((pump) => {
              const live = liveValues[pump.asset_id];
              const running = live ? Number(live.value) === 1 : null;
              return (
                <Card key={pump.asset_id} title={pump.name} subtitle={pump.location_label} testid={`card-pump-${pump.asset_id}`}>
                  <div className="flex flex-col items-center gap-3">
                    <div className="flex justify-center py-1">
                      {live ? (
                        <StatusCircle status={running ? "running" : "stopped"} subtitle={running ? "Running" : "Stopped"} />
                      ) : (
                        <div className="text-center">
                          <div className="text-xs text-gray-500 mb-2 uppercase tracking-wider font-bold">
                            {pump.gateway_id ? "Awaiting data" : "Not wired to live data"}
                          </div>
                          <div className="text-xs text-gray-500">Static: <span className="text-gray-700 font-semibold">{pump.status || "active"}</span></div>
                        </div>
                      )}
                    </div>
                    {live && (
                      <div className="text-xs space-y-1 w-full">
                        <div className="bg-gray-50 rounded p-2"><div className="text-gray-500 uppercase text-[10px] tracking-wider">Type</div><div className="font-semibold text-gray-900">{pump.meta?.pump_type || "—"}</div></div>
                        <div className="bg-gray-50 rounded p-2"><div className="text-gray-500 uppercase text-[10px] tracking-wider">Power (KW)</div><div className="font-semibold text-gray-900">{pump.meta?.power_kw || "—"}</div></div>
                        {/* Pump Trip / Auto-Manual are independent signals from the pump's primary
                            run/stop register (which already covers Pump Run Detection) — only
                            rendered if linked, same pattern as LiftsModule.jsx's extra-register grid. */}
                        {live.extra?.pump_trip && (
                          <div className="bg-gray-50 rounded p-2"><div className="text-gray-500 uppercase text-[10px] tracking-wider">Trip</div><div className={`font-semibold ${Number(live.extra.pump_trip.value) === 1 ? "text-red-600" : "text-emerald-600"}`}>{Number(live.extra.pump_trip.value) === 1 ? "TRIPPED" : "Normal"}</div></div>
                        )}
                        {live.extra?.auto_manual && (
                          <div className="bg-gray-50 rounded p-2"><div className="text-gray-500 uppercase text-[10px] tracking-wider">Auto/Manual</div><div className="font-semibold text-gray-900">{Number(live.extra.auto_manual.value) === 1 ? "Auto" : "Manual"}</div></div>
                        )}
                      </div>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        </section>
      )}

      {sprinklerSensors.length > 0 && (
        <section>
          <h2 className="text-sm uppercase tracking-[0.2em] text-gray-500 mb-3">Sprinkler Pressure Sensors</h2>
          <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
            {sprinklerSensors.map((sensor) => {
              const live = liveValues[sensor.asset_id];
              return (
                <Card key={sensor.asset_id} title={sensor.name} subtitle={sensor.location_label} testid={`card-sensor-${sensor.asset_id}`}>
                  {live ? (
                    <div className="max-w-[220px] mx-auto">
                      <PressureGauge value={Number(live.value)} max={sensor.meta?.range_bar || 16} threshold={sensor.meta?.alarm_low_bar || 5} label={live.unit || "bar"} critical />
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <div className="text-xs text-gray-500 mb-2 uppercase tracking-wider font-bold">
                        {sensor.gateway_id ? "Awaiting data" : "Not wired to live data"}
                      </div>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </section>
      )}

      {sprinklerValves.length > 0 && (
        <section>
          <h2 className="text-sm uppercase tracking-[0.2em] text-gray-500 mb-3">Sprinkler Valves</h2>
          <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
            {sprinklerValves.map((v) => {
              const live = liveValues[v.asset_id];
              const open = live ? Number(live.value) === 1 : null;
              return (
                <Card key={v.asset_id} title={v.name} subtitle={v.location_label} testid={`card-valve-${v.asset_id}`}>
                  <div className="flex items-center gap-4">
                    <span className={`w-4 h-4 rounded-full ${open ? "bg-emerald-500" : "bg-slate-500"}`} />
                    <div className="flex-1">
                      <div className="text-xl font-extrabold uppercase tracking-wider">
                        {open === null ? (v.gateway_id ? "Awaiting data" : "Not linked") : open ? "Open" : "Closed"}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">{v.meta?.type || "—"}</div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </section>
      )}

      {!sprinklerPumps.length && !sprinklerSensors.length && !sprinklerValves.length && (
        <div className="glass rounded-lg p-10 text-center text-gray-500 text-sm">
          No sprinkler pumps, pressure sensors, or valves registered yet. <a href="/pumps" className="text-orange-600 hover:text-orange-700 font-semibold">Add them in the Asset Registry</a>
        </div>
      )}

      {hist.length > 0 && (
        <div className="glass p-5 rounded-lg">
          <h3 className="font-semibold mb-3">{trendSensor?.name || "Sprinkler Riser Pressure"} — Last 24 Hours</h3>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={hist}>
              <CartesianGrid stroke="rgba(0,0,0,0.05)" strokeDasharray="3 3" />
              <XAxis dataKey="time" stroke="#94a3b8" fontSize={10} />
              <YAxis domain={[0, trendSensor?.meta?.range_bar || 16]} stroke="#94a3b8" fontSize={10} />
              <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #e2e8f0", color: "#1f2937" }} formatter={(v) => [`${Number(v).toFixed(2)} bar`, ""]} />
              <ReferenceLine y={trendSensor?.meta?.alarm_low_bar || 5} stroke="#ff3366" strokeDasharray="5 5" label={{ value: `GR ${trendSensor?.meta?.alarm_low_bar || 5} Bar`, position: "right", fill: "#ff3366", fontSize: 10 }} />
              <Line type="monotone" dataKey="value" stroke="#4fd1ff" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}


      {sprinklerPumps.length > 0 && (
        <ComplianceTable
          title="Section C — Sprinkler System (GR Appendix d)"
          rows={sprinklerPumps.map((pump) => {
            const live = liveValues[pump.asset_id];
            const running = live ? Number(live.value) === 1 : null;
            return {
              parameter: pump.name,
              current: live ? (running ? "Running" : "Stopped") : "—",
              required: "On-demand",
              ok: live ? running : pump.status === "active"
            };
          })}
        />
      )}
    </div>
  );
}
